import React from "react";
import { Helmet } from "react-helmet";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Clock, Newspaper, TrendingUp, Bell, Mail, BookOpen, ArrowRight, Rss } from "lucide-react";

const BlogNews = () => {
  const [email, setEmail] = React.useState("");

  const handleNotifyMe = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Implement email subscription
    console.log("Email subscription:", email);
    setEmail("");
  };

  const upcomingContent = [
    {
      title: "The Future of AI in Healthcare: 2024 Trends",
      description: "Industry insights and predictions for AI adoption in healthcare settings",
      category: "Industry Insights",
      icon: TrendingUp,
      readTime: "8 min read",
      type: "Featured Article"
    },
    {
      title: "Product Updates: New Monitoring Capabilities",
      description: "Latest enhancements to our AI-powered patient monitoring platform",
      category: "Product News",
      icon: Bell,
      readTime: "5 min read",
      type: "Product Update"
    },
    {
      title: "Healthcare AI Regulation: What You Need to Know",
      description: "Navigating FDA guidelines and compliance requirements for AI in healthcare",
      category: "Regulatory",
      icon: BookOpen,
      readTime: "12 min read",
      type: "Deep Dive"
    }
  ];

  const contentCategories = [
    {
      name: "Industry Insights",
      description: "Latest trends and analysis in healthcare AI",
      icon: TrendingUp
    },
    {
      name: "Product Updates",
      description: "New features, improvements, and releases",
      icon: Bell
    },
    {
      name: "Clinical Stories",
      description: "Real experiences from healthcare professionals",
      icon: BookOpen
    },
    {
      name: "Regulatory News",
      description: "Compliance updates and regulatory guidance",
      icon: Newspaper
    }
  ];

  return (
    <PageContainer>
      <Helmet>
        <title>Blog & News - Coming Soon | Healthcare AI Insights</title>
        <meta name="description" content="Stay updated with the latest healthcare AI news, industry insights, product updates, and regulatory developments. Blog and news coming soon." />
        <link rel="canonical" href="/resources/blog-news" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-20 lg:py-32">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex items-center justify-center mb-6">
              <Clock className="h-8 w-8 text-primary mr-3" />
              <span className="text-primary font-semibold">Coming Soon</span>
            </div>
            
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">
              Stay Informed
              <span className="block bg-gradient-to-r from-primary via-primary/80 to-primary/60 bg-clip-text text-transparent">
                Healthcare AI News & Insights
              </span>
            </h1>
            
            <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Get the latest updates on healthcare AI trends, product developments, regulatory changes, 
              and industry insights from our team of experts and healthcare professionals.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <form onSubmit={handleNotifyMe} className="flex gap-2">
                <Input
                  type="email"
                  placeholder="Subscribe to our newsletter"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="min-w-[300px]"
                  required
                />
                <Button type="submit" className="px-6">
                  <Rss className="h-4 w-4 mr-2" />
                  Subscribe
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Upcoming Content */}
      <section className="py-16 bg-muted/50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">What's Coming</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Our editorial team is preparing comprehensive coverage of healthcare AI developments, 
                from technical insights to regulatory updates.
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              {upcomingContent.map((article, index) => (
                <Card key={index} className="group hover:shadow-lg transition-all duration-300">
                  <CardHeader>
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 bg-primary/10 rounded-lg">
                        <article.icon className="h-6 w-6 text-primary" />
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium text-primary">{article.type}</div>
                        <div className="text-sm text-muted-foreground">{article.readTime}</div>
                      </div>
                    </div>
                    <CardTitle className="text-xl group-hover:text-primary transition-colors">
                      {article.title}
                    </CardTitle>
                    <CardDescription className="text-base">
                      {article.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{article.category}</span>
                      <div className="flex items-center text-primary font-medium group-hover:translate-x-1 transition-transform">
                        Read More <ArrowRight className="h-4 w-4 ml-2" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Content Categories */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Content Categories</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Our blog will cover diverse topics to keep you informed about all aspects 
                of AI in healthcare.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {contentCategories.map((category, index) => (
                <Card key={index} className="text-center group hover:shadow-lg transition-all duration-300">
                  <CardHeader>
                    <div className="p-4 bg-primary/10 rounded-lg w-fit mx-auto mb-4 group-hover:bg-primary group-hover:text-white transition-colors">
                      <category.icon className="h-8 w-8" />
                    </div>
                    <CardTitle className="text-lg">{category.name}</CardTitle>
                    <CardDescription>{category.description}</CardDescription>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Features */}
      <section className="py-16 bg-muted/50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">What to Expect</h2>
              <p className="text-lg text-muted-foreground">
                Our newsletter and blog will provide valuable insights tailored for healthcare professionals and industry leaders.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <h3 className="text-xl font-semibold">Newsletter Content:</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Weekly industry news roundup</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Product feature highlights and updates</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Regulatory and compliance updates</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Upcoming webinar and event notifications</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Exclusive insights from our clinical team</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-6 rounded-lg">
                <h4 className="font-semibold mb-4">Blog Features</h4>
                <div className="space-y-3 text-muted-foreground">
                  <div>• In-depth technical articles</div>
                  <div>• Customer success stories</div>
                  <div>• Industry trend analysis</div>
                  <div>• Best practices and how-to guides</div>
                  <div>• Thought leadership pieces</div>
                  <div>• Research study summaries</div>
                </div>
                
                <div className="mt-6 p-4 bg-white/50 rounded-lg">
                  <div className="font-semibold mb-2">Publishing Schedule:</div>
                  <div className="text-sm space-y-1">
                    <div>• 3-4 blog posts per week</div>
                    <div>• Weekly newsletter every Friday</div>
                    <div>• Breaking news alerts as needed</div>
                    <div>• Monthly feature articles</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Never Miss an Update</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Join thousands of healthcare professionals who stay informed with our comprehensive 
              coverage of AI in healthcare.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="px-8">
                <Mail className="h-5 w-5 mr-2" />
                Subscribe to Newsletter
              </Button>
              <Button variant="outline" size="lg" className="px-8">
                <Rss className="h-5 w-5 mr-2" />
                Follow RSS Feed
              </Button>
            </div>
          </div>
        </div>
      </section>
    </PageContainer>
  );
};

export default BlogNews;